import { useCallback, useEffect, useState } from 'react';

import { AutoComplete, AutoCompleteProps } from 'primereact/autocomplete';

import {
  useGetSitesQuery,
  useLazyGetSitesSearchQuery,
} from '@/app/services/lookups';
import { MIN_SEARCH_LENGTH, SELECTION_LIMIT } from '@/constants';
import { Site } from '@/rtk-query.gql';

import './SitesDropdown.css';

type SitesAutoCompleteProps = AutoCompleteProps & {
  className?: string;
  label?: string;
  defaultValue?: Site;
  defaultValueId?: string;
};

export const SitesAutoComplete = ({
  className,
  label,
  defaultValue,
  defaultValueId,
  onSelect: externalOnSelect,
  onChange: externalOnChange,
  ...autoCompleteProps
}: SitesAutoCompleteProps) => {
  const [selected, setSelected] = useState<Site | undefined>(defaultValue);
  const [filtered, setFiltered] = useState<Site[]>([]);
  const [inputValue, setInputValue] = useState(defaultValue?.name);

  const { data: siteData } = useGetSitesQuery(
    {
      where: { id: { eq: defaultValueId } },
    },
    { skip: !defaultValueId },
  );
  const siteName = siteData?.[0]?.name;

  const [query] = useLazyGetSitesSearchQuery();

  useEffect(() => {
    setSelected(defaultValue);
    setInputValue(defaultValue?.name || '');
  }, [defaultValue]);

  useEffect(() => {
    if (siteData?.[0]) {
      setSelected(siteData?.[0] as Site);
      setInputValue(siteData?.[0]?.name || '');
    }
  }, [siteData]);

  const search = useCallback(
    async (event: { query: string }) => {
      if (event.query == null) {
        setFiltered([]);
        return;
      }

      const searchTerm = event.query;

      const { data = [] } = await query({
        skip: 0,
        take: SELECTION_LIMIT,
        searchTerm: searchTerm || '',
      });

      setFiltered(data);
    },
    [query],
  );

  const handleSelect = (e: { value: Site }) => {
    setSelected(e.value);
    setInputValue(e.value?.name as string);
    externalOnSelect?.(e as any);
  };

  const handleChange = (e: { value: string }) => {
    setInputValue(e.value as string);
    externalOnChange?.(e as any);
  };

  return (
    <div
      className={`[ storage-location-auto-complete ] [${className ? ` ${className}` : ''} label-input-group ]`}
    >
      {label && <label>{label}</label>}
      <AutoComplete
        appendTo="self"
        suggestions={filtered}
        field="name"
        minLength={MIN_SEARCH_LENGTH}
        selectionLimit={SELECTION_LIMIT}
        completeMethod={search}
        onSelect={handleSelect}
        onChange={handleChange}
        dropdown={true}
        {...autoCompleteProps}
        value={inputValue}
      />
    </div>
  );
};
